import {
  STUDY_MIND_MAP_LIMITS,
  STUDY_MIND_MAP_NODE_KINDS,
  STUDY_MIND_MAP_SIDES,
  type StudyMindMapColor,
  type StudyMindMapContent,
  type StudyMindMapDraft,
  type StudyMindMapEdge,
  type StudyMindMapNode,
  type StudyMindMapPreview,
  type StudyMindMapSide,
} from './studyMindMapApi';
import { edgeStyleFields } from './studyMindMapLinkStyle';

/**
 * Pure mind map editing rules (study-mind-map-prd.md §4.2, §4.5, §5.2). Every editor action and
 * the text view call these, so the canvas and the text view always change the same content.
 * Nothing here does I/O; each rule has a unit test.
 */

/** Canvas size of one idea card and the gaps of the Tidy up tree layout. */
export const MIND_MAP_NODE_WIDTH = 200;
export const MIND_MAP_COLUMN_GAP = 260;
export const MIND_MAP_ROW_GAP = 96;
/** Space between two separate trees after Tidy up. */
const TREE_GAP = MIND_MAP_ROW_GAP;
/** Where inserted ideas go when nothing is selected: right of everything on the map. */
const INSERT_MARGIN = 320;
/** Undo keeps this many steps. */
export const MIND_MAP_HISTORY_LIMIT = 100;
/** Text of an idea the learner adds before typing. */
export const NEW_IDEA_TEXT = 'New idea';

const COORDINATE_LIMIT = 1_000_000;
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function emptyMindMapContent(): StudyMindMapContent {
  return { nodes: [], edges: [], viewport: { x: 0, y: 0, zoom: 1 } };
}

let idCounter = 0;

/** A new id that is not used on this map yet (letters, digits, `_` and `-` only). */
export function newElementId(prefix: 'n' | 'e', taken: ReadonlySet<string>): string {
  for (;;) {
    idCounter = (idCounter + 1) % 1_000_000;
    const id = `${prefix}${Date.now().toString(36)}${idCounter.toString(36)}${Math.floor(
      Math.random() * 1296,
    ).toString(36)}`;
    if (!taken.has(id) && ID_PATTERN.test(id)) return id;
  }
}

function nodeIds(content: StudyMindMapContent): Set<string> {
  return new Set(content.nodes.map((node) => node.id));
}

function edgeIds(content: StudyMindMapContent): Set<string> {
  return new Set(content.edges.map((edge) => edge.id));
}

/** A whole number within bounds; a missing or broken value becomes the minimum. */
function clampBetween(value: number | undefined, min: number, max: number): number {
  if (value === undefined || !Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.round(value)));
}

function clampCoordinate(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(-COORDINATE_LIMIT, Math.min(COORDINATE_LIMIT, Math.round(value)));
}

/** True when another idea may be added (PRD §3: 300 ideas per map). */
export function canAddNode(content: StudyMindMapContent): boolean {
  return content.nodes.length < STUDY_MIND_MAP_LIMITS.nodes_per_map;
}

export interface AddResult {
  content: StudyMindMapContent;
  /** Null when the map is full. */
  id: string | null;
}

/** Most cards tried below the requested point before the idea is placed there anyway. */
const FREE_POSITION_TRIES = 40;
/** Space kept between two cards in a column when a new idea is placed. */
export const MIND_MAP_CARD_GAP = 24;
/** Card height used when the real height is not known; with the gap it equals one row. */
const DEFAULT_CARD_HEIGHT = MIND_MAP_ROW_GAP - MIND_MAP_CARD_GAP;

export interface FreePositionOptions {
  /** Height of an idea on the map, for example as measured on screen. */
  heightOf?: (node: StudyMindMapNode) => number;
  /** Expected height of the new card. */
  height?: number;
}

/**
 * The first point at or below `position` where the new card touches no other card, with
 * `MIND_MAP_CARD_GAP` free above and below it. Ideas added one after another at the same point
 * (repeated `+`, palette clicks) form a column instead of a stack. Group frames are containers,
 * so an idea may go inside one.
 */
export function freePosition(
  content: StudyMindMapContent,
  position: { x: number; y: number },
  options: FreePositionOptions = {},
): { x: number; y: number } {
  const heightOf = options.heightOf ?? (() => DEFAULT_CARD_HEIGHT);
  const height = options.height ?? DEFAULT_CARD_HEIGHT;
  let y = position.y;
  for (let tries = 0; tries < FREE_POSITION_TRIES; tries += 1) {
    const top = y;
    const hits = content.nodes.filter(
      (node) =>
        node.kind !== 'group' &&
        Math.abs(node.x - position.x) < MIND_MAP_NODE_WIDTH &&
        node.y < top + height + MIND_MAP_CARD_GAP &&
        top < node.y + heightOf(node) + MIND_MAP_CARD_GAP,
    );
    if (hits.length === 0) return { x: position.x, y };
    y = Math.max(...hits.map((node) => node.y + heightOf(node))) + MIND_MAP_CARD_GAP;
  }
  return position;
}

/** Adds a learner idea at a canvas point (double-click on empty canvas, or `+`). */
export function addNode(
  content: StudyMindMapContent,
  position: { x: number; y: number },
  text = NEW_IDEA_TEXT,
  options: FreePositionOptions = {},
): AddResult {
  if (!canAddNode(content)) return { content, id: null };
  const id = newElementId('n', nodeIds(content));
  const free = freePosition(content, position, options);
  const node: StudyMindMapNode = {
    id,
    text,
    x: clampCoordinate(free.x),
    y: clampCoordinate(free.y),
  };
  return { content: { ...content, nodes: [...content.nodes, node] }, id };
}

/** The ideas joined from this idea, in link order. */
export function childIds(content: StudyMindMapContent, parentId: string): string[] {
  return content.edges.filter((edge) => edge.from === parentId).map((edge) => edge.to);
}

/** The first idea that links to this one, or null for a top idea. */
export function parentId(content: StudyMindMapContent, id: string): string | null {
  return content.edges.find((edge) => edge.to === id)?.from ?? null;
}

/** `Tab` / `Add child`: a new idea joined under the selected one. */
export function addChild(content: StudyMindMapContent, parent: string): AddResult {
  const parentNode = content.nodes.find((node) => node.id === parent);
  if (!parentNode || !canAddNode(content)) return { content, id: null };
  const children = childIds(content, parent)
    .map((id) => content.nodes.find((node) => node.id === id))
    .filter((node): node is StudyMindMapNode => node !== undefined);
  const y = children.length
    ? Math.max(...children.map((node) => node.y)) + MIND_MAP_ROW_GAP
    : parentNode.y;
  const added = addNode(content, { x: parentNode.x + MIND_MAP_COLUMN_GAP, y });
  if (!added.id) return added;
  return { content: connect(added.content, parent, added.id), id: added.id };
}

/** `Enter`: a new idea next to the selected one, under the same parent. */
export function addSibling(content: StudyMindMapContent, id: string): AddResult {
  const node = content.nodes.find((item) => item.id === id);
  if (!node) return { content, id: null };
  const parent = parentId(content, id);
  if (parent) return addChild(content, parent);
  return addNode(content, { x: node.x, y: node.y + MIND_MAP_ROW_GAP * 1.5 });
}

export function updateNode(
  content: StudyMindMapContent,
  id: string,
  patch: Partial<Omit<StudyMindMapNode, 'id'>>,
): StudyMindMapContent {
  return {
    ...content,
    nodes: content.nodes.map((node) => {
      if (node.id !== id) return node;
      const next: StudyMindMapNode = { ...node, ...patch };
      // Optional fields are removed, never sent as `undefined` or `null`.
      for (const key of [
        'color',
        'unsure',
        'note',
        'source',
        'origin',
        'kind',
        'w',
        'h',
      ] as const) {
        if (next[key] === undefined || next[key] === null) delete next[key];
      }
      if (next.unsure === false) delete next.unsure;
      if (next.color === 'neutral') delete next.color;
      if (next.kind === 'idea') delete next.kind;
      return next;
    }),
  };
}

/** Sets idea text. Empty text keeps the old text: an idea always has words. */
export function setNodeText(
  content: StudyMindMapContent,
  id: string,
  text: string,
): StudyMindMapContent {
  const trimmed = text.trim().slice(0, STUDY_MIND_MAP_LIMITS.node_text_max);
  const node = content.nodes.find((item) => item.id === id);
  if (!trimmed || !node || node.text === trimmed) return content;
  return updateNode(content, id, { text: trimmed });
}

export function moveNodes(
  content: StudyMindMapContent,
  positions: ReadonlyMap<string, { x: number; y: number }>,
): StudyMindMapContent {
  if (positions.size === 0) return content;
  return {
    ...content,
    nodes: content.nodes.map((node) => {
      const position = positions.get(node.id);
      return position
        ? { ...node, x: clampCoordinate(position.x), y: clampCoordinate(position.y) }
        : node;
    }),
  };
}

/** Deletes an idea and every link to or from it. */
export function removeNode(content: StudyMindMapContent, id: string): StudyMindMapContent {
  return {
    ...content,
    nodes: content.nodes.filter((node) => node.id !== id),
    edges: content.edges.filter((edge) => edge.from !== id && edge.to !== id),
  };
}

/**
 * Joins two ideas. Two ideas may have any number of links, in either direction and between any
 * handles (owner feedback 2026-10-09). A link to itself, a link to an idea that is not on the
 * map, or a link past the map limit changes nothing.
 */
export function connect(
  content: StudyMindMapContent,
  from: string,
  to: string,
  label?: string,
  sides: LinkHandleSides = {},
): StudyMindMapContent {
  if (connectRefusal(content, from, to) !== null) return content;
  const edge: StudyMindMapEdge = { id: newElementId('e', edgeIds(content)), from, to };
  const cleanLabel = label?.trim().slice(0, STUDY_MIND_MAP_LIMITS.edge_label_max);
  if (cleanLabel) edge.label = cleanLabel;
  return { ...content, edges: [...content.edges, withSides(edge, sides)] };
}

/**
 * Re-points one end (or both) of a link the learner dragged by its end. The words and style stay.
 * Refused (unchanged) only for a self link or an idea that is not on the map; another link
 * between the same ideas is fine (owner feedback 2026-10-09).
 */
export function reconnectEdge(
  content: StudyMindMapContent,
  edgeId: string,
  from: string,
  to: string,
  sides: LinkHandleSides,
): StudyMindMapContent {
  const edge = content.edges.find((e) => e.id === edgeId);
  if (!edge || from === to) return content;
  const ids = nodeIds(content);
  if (!ids.has(from) || !ids.has(to)) return content;
  const next = withSides({ ...edge, from, to }, sides);
  return { ...content, edges: content.edges.map((e) => (e.id === edgeId ? next : e)) };
}

/** The handles a learner joined. A side is absent when the link was not made handle to handle. */
export interface LinkHandleSides {
  from?: MindMapSide | null;
  to?: MindMapSide | null;
}

export function isMindMapSide(value: unknown): value is MindMapSide {
  return typeof value === 'string' && (STUDY_MIND_MAP_SIDES as readonly string[]).includes(value);
}

/** The edge with only the valid sides of `sides` (owner feedback 2026-10-09). */
function withSides(edge: StudyMindMapEdge, sides: LinkHandleSides): StudyMindMapEdge {
  const next: StudyMindMapEdge = { ...edge };
  delete next.from_side;
  delete next.to_side;
  if (isMindMapSide(sides.from)) next.from_side = sides.from;
  if (isMindMapSide(sides.to)) next.to_side = sides.to;
  return next;
}

/**
 * Why a link between two ideas is not made, or null when it can be made. There is no rule about
 * links already between the two ideas: the earlier one-link-per-pair rule was an unrequested
 * implementation choice and is removed (owner feedback 2026-10-09).
 */
export type ConnectRefusal = 'self' | 'missing' | 'limit';

export function connectRefusal(
  content: StudyMindMapContent,
  from: string,
  to: string,
): ConnectRefusal | null {
  if (from === to) return 'self';
  const ids = nodeIds(content);
  if (!ids.has(from) || !ids.has(to)) return 'missing';
  if (content.edges.length >= STUDY_MIND_MAP_LIMITS.edges_per_map) return 'limit';
  return null;
}

/**
 * The idea a link drag ends on when the pointer is released over an idea card instead of a
 * handle (owner feedback 2026-10-09). Null when there is no idea there or the link is refused.
 */
export function dropConnectTarget(
  content: StudyMindMapContent,
  from: string,
  droppedOn: string | null,
): string | null {
  if (!droppedOn) return null;
  return connectRefusal(content, from, droppedOn) === null ? droppedOn : null;
}

/** Sides of an idea card where a link starts or ends. */
export type MindMapSide = StudyMindMapSide;
/** Typical card height, used only to compare positions; the real height varies with text. */
export const MIND_MAP_NODE_HEIGHT = 56;

function centre(node: Pick<StudyMindMapNode, 'x' | 'y'>) {
  return { x: node.x + MIND_MAP_NODE_WIDTH / 2, y: node.y + MIND_MAP_NODE_HEIGHT / 2 };
}

/**
 * The shortest pair of sides for a link (owner feedback 2026-10-09): ideas side by side join
 * right-to-left, ideas above each other join bottom-to-top, so a line never loops around a card.
 * Card width and height are both taken into account so wide cards prefer the left and right.
 */
export function linkSides(
  from: Pick<StudyMindMapNode, 'x' | 'y'>,
  to: Pick<StudyMindMapNode, 'x' | 'y'>,
): { source: MindMapSide; target: MindMapSide } {
  const a = centre(from);
  const b = centre(to);
  const dx = (b.x - a.x) / MIND_MAP_NODE_WIDTH;
  const dy = (b.y - a.y) / MIND_MAP_NODE_HEIGHT;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? { source: 'right', target: 'left' } : { source: 'left', target: 'right' };
  }
  return dy >= 0 ? { source: 'bottom', target: 'top' } : { source: 'top', target: 'bottom' };
}

/**
 * The sides a link is drawn between (owner feedback 2026-10-09): a side the learner chose
 * (`from_side` / `to_side`) always wins, also after the ideas move. Only a missing side falls
 * back to the facing side from `linkSides` (drop on a card, keyboard or toolbar connect, AI
 * draft, migrated and older links).
 */
export function edgeSides(
  edge: Pick<StudyMindMapEdge, 'from_side' | 'to_side'>,
  from: Pick<StudyMindMapNode, 'x' | 'y'>,
  to: Pick<StudyMindMapNode, 'x' | 'y'>,
): { source: MindMapSide; target: MindMapSide } {
  const chosenFrom = isMindMapSide(edge.from_side) ? edge.from_side : null;
  const chosenTo = isMindMapSide(edge.to_side) ? edge.to_side : null;
  if (chosenFrom && chosenTo) return { source: chosenFrom, target: chosenTo };
  const auto = linkSides(from, to);
  return { source: chosenFrom ?? auto.source, target: chosenTo ?? auto.target };
}

export type MindMapDirection = 'up' | 'down' | 'left' | 'right';

/**
 * The nearest idea in a direction from an idea (arrow keys, owner feedback 2026-10-09). Only
 * ideas whose centre lies in that direction count; distance across the direction weighs double,
 * so the idea most straight ahead wins. Null when there is no idea that way.
 */
export function nearestNodeInDirection(
  content: StudyMindMapContent,
  fromId: string,
  direction: MindMapDirection,
  exclude: ReadonlySet<string> = new Set(),
): string | null {
  const from = content.nodes.find((node) => node.id === fromId);
  if (!from) return null;
  const a = centre(from);
  let best: { id: string; score: number } | null = null;
  for (const node of content.nodes) {
    if (node.id === fromId || exclude.has(node.id)) continue;
    const b = centre(node);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const ahead =
      direction === 'right' ? dx : direction === 'left' ? -dx : direction === 'down' ? dy : -dy;
    const across = direction === 'left' || direction === 'right' ? Math.abs(dy) : Math.abs(dx);
    if (ahead <= 0) continue;
    const score = ahead + across * 2;
    if (!best || score < best.score) best = { id: node.id, score };
  }
  return best?.id ?? null;
}

/** Shift+arrow moves an idea by this many canvas points. */
export const MIND_MAP_NUDGE_STEP = 16;

/** Moves one idea a small step (Shift+arrow). */
export function nudgeNode(
  content: StudyMindMapContent,
  id: string,
  direction: MindMapDirection,
  step = MIND_MAP_NUDGE_STEP,
): StudyMindMapContent {
  const node = content.nodes.find((item) => item.id === id);
  if (!node) return content;
  const dx = direction === 'right' ? step : direction === 'left' ? -step : 0;
  const dy = direction === 'down' ? step : direction === 'up' ? -step : 0;
  return moveNodes(content, new Map([[id, { x: node.x + dx, y: node.y + dy }]]));
}

/** Where a duplicate goes, from the original idea. */
const DUPLICATE_OFFSET = 32;

/**
 * Cmd/Ctrl+D: a copy of one idea (words, notes, colour, mark and source) placed a little below
 * and right of it, with no links.
 */
export function duplicateNode(content: StudyMindMapContent, id: string): AddResult {
  const node = content.nodes.find((item) => item.id === id);
  if (!node || !canAddNode(content)) return { content, id: null };
  const copyId = newElementId('n', nodeIds(content));
  const copy: StudyMindMapNode = {
    ...node,
    id: copyId,
    x: clampCoordinate(node.x + DUPLICATE_OFFSET),
    y: clampCoordinate(node.y + DUPLICATE_OFFSET),
  };
  if (node.source) copy.source = { ...node.source };
  // A copy must not push the map past its notes limit.
  if (
    copy.note &&
    mindMapNotesLength(content) + copy.note.length > STUDY_MIND_MAP_LIMITS.notes_total_max
  )
    delete copy.note;
  return { content: { ...content, nodes: [...content.nodes, copy] }, id: copyId };
}

/** All note characters of the map together (the server allows `notes_total_max`). */
export function mindMapNotesLength(content: Pick<StudyMindMapContent, 'nodes'>): number {
  return content.nodes.reduce((total, node) => total + (node.note?.length ?? 0), 0);
}

/**
 * The longest note this idea may have: the per-note limit, or less when the other notes of the
 * map already use most of the map total.
 */
export function noteRoom(content: StudyMindMapContent, id: string): number {
  const own = content.nodes.find((node) => node.id === id)?.note?.length ?? 0;
  const others = mindMapNotesLength(content) - own;
  return Math.max(
    0,
    Math.min(STUDY_MIND_MAP_LIMITS.note_max, STUDY_MIND_MAP_LIMITS.notes_total_max - others),
  );
}

/** Sets or clears (empty text) the notes of an idea, within the note and map limits. */
export function setNodeNote(
  content: StudyMindMapContent,
  id: string,
  note: string,
): StudyMindMapContent {
  const node = content.nodes.find((item) => item.id === id);
  if (!node) return content;
  const clean = note.slice(0, noteRoom(content, id));
  const next = clean.trim() ? clean : undefined;
  if ((node.note ?? undefined) === next) return content;
  return updateNode(content, id, { note: next });
}

export function removeEdge(content: StudyMindMapContent, id: string): StudyMindMapContent {
  return { ...content, edges: content.edges.filter((edge) => edge.id !== id) };
}

/** Sets or clears (empty text) the words on a link. */
export function setEdgeLabel(
  content: StudyMindMapContent,
  id: string,
  label: string,
): StudyMindMapContent {
  const clean = label.trim().slice(0, STUDY_MIND_MAP_LIMITS.edge_label_max);
  return {
    ...content,
    edges: content.edges.map((edge) => {
      if (edge.id !== id) return edge;
      const next: StudyMindMapEdge = { ...edge };
      delete next.label;
      if (clean) next.label = clean;
      return next;
    }),
  };
}

export interface OutlineItem {
  node: StudyMindMapNode;
  depth: number;
  parentId: string | null;
}

/**
 * The map as a tree, for the Text view and Tidy up (PRD §4.5). Top ideas are the ones nothing
 * links to, in map order. Each idea appears once, under the first idea that reaches it; ideas
 * only reachable through a loop start their own tree.
 */
export function mindMapOutline(content: StudyMindMapContent): OutlineItem[] {
  const ids = nodeIds(content);
  const byId = new Map(content.nodes.map((node) => [node.id, node]));
  const children = new Map<string, string[]>();
  const hasParent = new Set<string>();
  for (const edge of content.edges) {
    if (!ids.has(edge.from) || !ids.has(edge.to) || edge.from === edge.to) continue;
    children.set(edge.from, [...(children.get(edge.from) ?? []), edge.to]);
    hasParent.add(edge.to);
  }
  const seen = new Set<string>();
  const items: OutlineItem[] = [];
  const visit = (id: string, depth: number, parent: string | null) => {
    if (seen.has(id)) return;
    seen.add(id);
    const node = byId.get(id);
    if (!node) return;
    items.push({ node, depth, parentId: parent });
    for (const child of children.get(id) ?? []) visit(child, depth + 1, id);
  };
  for (const node of content.nodes) if (!hasParent.has(node.id)) visit(node.id, 0, null);
  for (const node of content.nodes) visit(node.id, 0, null);
  return items;
}

/** Tree positions from an outline: depth sets the column, leaves take one row each. */
function treePositions(
  outline: OutlineItem[],
  origin: { x: number; y: number },
): Map<string, { x: number; y: number }> {
  const kids = new Map<string, string[]>();
  for (const item of outline) {
    if (item.parentId) kids.set(item.parentId, [...(kids.get(item.parentId) ?? []), item.node.id]);
  }
  const depth = new Map(outline.map((item) => [item.node.id, item.depth]));
  const positions = new Map<string, { x: number; y: number }>();
  let nextRow = origin.y;
  const place = (id: string): number => {
    const children = kids.get(id) ?? [];
    let y: number;
    if (children.length === 0) {
      y = nextRow;
      nextRow += MIND_MAP_ROW_GAP;
    } else {
      const ys = children.map(place);
      y = (ys[0] + ys[ys.length - 1]) / 2;
    }
    positions.set(id, { x: origin.x + (depth.get(id) ?? 0) * MIND_MAP_COLUMN_GAP, y });
    return y;
  };
  for (const item of outline) {
    if (item.parentId !== null) continue;
    place(item.node.id);
    nextRow += TREE_GAP;
  }
  return positions;
}

/** `Tidy up`: a deterministic left-to-right tree layout. Same map in, same positions out. */
export function tidyLayout(content: StudyMindMapContent): StudyMindMapContent {
  return moveNodes(content, treePositions(mindMapOutline(content), { x: 0, y: 0 }));
}

/** Draft ideas the learner kept in the preview, with the links between kept ideas only. */
function keptDraft(draft: Pick<StudyMindMapDraft, 'nodes' | 'edges'>, kept: ReadonlySet<string>) {
  const nodes = draft.nodes.filter((node) => kept.has(node.temp_id));
  const edges = draft.edges.filter(
    (edge) => kept.has(edge.from) && kept.has(edge.to) && edge.from !== edge.to,
  );
  return { nodes, edges };
}

/**
 * Turns kept draft ideas into map content with new ids, `origin: 'ai'` and their sources
 * (PRD §5.1). Used for a new map and for "Add from materials".
 */
function draftContent(
  draft: Pick<StudyMindMapDraft, 'nodes' | 'edges'>,
  kept: ReadonlySet<string>,
  takenNodes: Set<string>,
  takenEdges: Set<string>,
): { content: StudyMindMapContent; idMap: Map<string, string> } {
  const { nodes, edges } = keptDraft(draft, kept);
  const idMap = new Map<string, string>();
  const content = emptyMindMapContent();
  for (const node of nodes) {
    const id = newElementId('n', takenNodes);
    takenNodes.add(id);
    idMap.set(node.temp_id, id);
    content.nodes.push({
      id,
      text: node.text.trim().slice(0, STUDY_MIND_MAP_LIMITS.node_text_max) || NEW_IDEA_TEXT,
      x: 0,
      y: 0,
      origin: 'ai',
      source: { ...node.source },
    });
  }
  const seenPairs = new Set<string>();
  for (const edge of edges) {
    const from = idMap.get(edge.from);
    const to = idMap.get(edge.to);
    if (!from || !to) continue;
    const pair = [from, to].sort().join('|');
    if (seenPairs.has(pair)) continue;
    seenPairs.add(pair);
    const id = newElementId('e', takenEdges);
    takenEdges.add(id);
    const label = edge.label?.trim().slice(0, STUDY_MIND_MAP_LIMITS.edge_label_max);
    content.edges.push(label ? { id, from, to, label } : { id, from, to });
  }
  return { content, idMap };
}

/** A new map from an AI draft ("Create map"), laid out by Tidy up. */
export function mapFromDraft(
  draft: Pick<StudyMindMapDraft, 'nodes' | 'edges'>,
  kept: ReadonlySet<string>,
): StudyMindMapContent {
  return tidyLayout(draftContent(draft, kept, new Set(), new Set()).content);
}

/**
 * "Add from materials" (PRD §5.2): appends kept draft ideas. With an anchor they hang under the
 * selected idea, below its other children; without one they go right of the whole map. Existing
 * ideas and links are never changed or removed.
 */
export function insertDraft(
  content: StudyMindMapContent,
  draft: Pick<StudyMindMapDraft, 'nodes' | 'edges'>,
  kept: ReadonlySet<string>,
  anchorId: string | null,
): StudyMindMapContent {
  const takenNodes = nodeIds(content);
  const takenEdges = edgeIds(content);
  const inserted = draftContent(draft, kept, takenNodes, takenEdges).content;
  if (inserted.nodes.length === 0) return content;
  const anchor = anchorId ? content.nodes.find((node) => node.id === anchorId) : undefined;

  let origin: { x: number; y: number };
  if (anchor) {
    const children = childIds(content, anchor.id)
      .map((id) => content.nodes.find((node) => node.id === id))
      .filter((node): node is StudyMindMapNode => node !== undefined);
    origin = {
      x: anchor.x + MIND_MAP_COLUMN_GAP,
      y: children.length
        ? Math.max(...children.map((node) => node.y)) + MIND_MAP_ROW_GAP
        : anchor.y,
    };
  } else if (content.nodes.length) {
    origin = {
      x: Math.max(...content.nodes.map((node) => node.x)) + INSERT_MARGIN,
      y: Math.min(...content.nodes.map((node) => node.y)),
    };
  } else {
    origin = { x: 0, y: 0 };
  }
  const outline = mindMapOutline(inserted);
  const positions = treePositions(outline, origin);
  const placed = moveNodes(inserted, positions);
  const nodes = [...content.nodes, ...placed.nodes];
  const edges = [...content.edges, ...placed.edges];
  if (anchor) {
    for (const item of outline) {
      if (item.parentId !== null || edges.length >= STUDY_MIND_MAP_LIMITS.edges_per_map) continue;
      const id = newElementId('e', takenEdges);
      takenEdges.add(id);
      edges.push({ id, from: anchor.id, to: item.node.id });
    }
  }
  return { ...content, nodes, edges };
}

/**
 * The content exactly as the server accepts it: trimmed words within limits, no empty optional
 * fields, links only between ideas on the map, whole-number coordinates and a valid zoom.
 */
export function contentForSave(content: StudyMindMapContent): StudyMindMapContent {
  const nodes = content.nodes
    .map((node) => {
      const text = node.text.trim().slice(0, STUDY_MIND_MAP_LIMITS.node_text_max);
      const next: StudyMindMapNode = {
        id: node.id,
        text: text || NEW_IDEA_TEXT,
        x: clampCoordinate(node.x),
        y: clampCoordinate(node.y),
      };
      if (node.color && node.color !== 'neutral') next.color = node.color;
      if (node.unsure) next.unsure = true;
      if (node.note) next.note = node.note.slice(0, STUDY_MIND_MAP_LIMITS.note_max);
      if (node.origin) next.origin = node.origin;
      if (node.source) next.source = node.source;
      const kind = (STUDY_MIND_MAP_NODE_KINDS as readonly string[]).includes(node.kind ?? '')
        ? node.kind
        : undefined;
      if (kind && kind !== 'idea') next.kind = kind;
      if (kind === 'group') {
        next.w = clampBetween(
          node.w,
          STUDY_MIND_MAP_LIMITS.group_min_w,
          STUDY_MIND_MAP_LIMITS.group_max_w,
        );
        next.h = clampBetween(
          node.h,
          STUDY_MIND_MAP_LIMITS.group_min_h,
          STUDY_MIND_MAP_LIMITS.group_max_h,
        );
      }
      return next;
    })
    .filter((node) => ID_PATTERN.test(node.id));
  const ids = new Set(nodes.map((node) => node.id));
  const edges = content.edges
    .filter((edge) => ids.has(edge.from) && ids.has(edge.to) && edge.from !== edge.to)
    .map((edge) => {
      const label = edge.label?.trim().slice(0, STUDY_MIND_MAP_LIMITS.edge_label_max);
      const next: StudyMindMapEdge = label
        ? { id: edge.id, from: edge.from, to: edge.to, label }
        : { id: edge.id, from: edge.from, to: edge.to };
      return {
        ...withSides(next, { from: edge.from_side, to: edge.to_side }),
        ...edgeStyleFields(edge),
      };
    });
  const zoom = Number.isFinite(content.viewport.zoom) ? content.viewport.zoom : 1;
  return {
    nodes,
    edges,
    viewport: {
      x: clampCoordinate(content.viewport.x),
      y: clampCoordinate(content.viewport.y),
      zoom: Math.max(0.05, Math.min(8, zoom)),
    },
  };
}

/** Undo and redo (PRD §4.2). Only ideas and links are undone; the view position is not. */
export interface MindMapHistory {
  past: StudyMindMapContent[];
  present: StudyMindMapContent;
  future: StudyMindMapContent[];
}

export function startHistory(content: StudyMindMapContent): MindMapHistory {
  return { past: [], present: content, future: [] };
}

/** A new editing step. The same content again is not a step. */
export function pushHistory(history: MindMapHistory, next: StudyMindMapContent): MindMapHistory {
  if (next === history.present) return history;
  return {
    past: [...history.past, history.present].slice(-MIND_MAP_HISTORY_LIMIT),
    present: next,
    future: [],
  };
}

/** Replaces the current content without a new step, for example while dragging. */
export function replacePresent(history: MindMapHistory, next: StudyMindMapContent): MindMapHistory {
  return { ...history, present: next };
}

/** Ends a drag: the content before the drag becomes one undo step. */
export function commitStep(history: MindMapHistory, before: StudyMindMapContent): MindMapHistory {
  if (before === history.present) return history;
  return {
    past: [...history.past, before].slice(-MIND_MAP_HISTORY_LIMIT),
    present: history.present,
    future: [],
  };
}

export function undoHistory(history: MindMapHistory): MindMapHistory {
  const previous = history.past[history.past.length - 1];
  if (!previous) return history;
  return {
    past: history.past.slice(0, -1),
    present: { ...previous, viewport: history.present.viewport },
    future: [history.present, ...history.future],
  };
}

export function redoHistory(history: MindMapHistory): MindMapHistory {
  const next = history.future[0];
  if (!next) return history;
  return {
    past: [...history.past, history.present],
    present: { ...next, viewport: history.present.viewport },
    future: history.future.slice(1),
  };
}

export interface PreviewBox {
  nodes: Array<{ id: string; x: number; y: number; color?: StudyMindMapColor; text: string }>;
  edges: Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    color?: StudyMindMapEdge['color'];
    line?: StudyMindMapEdge['line'];
  }>;
  width: number;
  height: number;
}

/**
 * Scales a map preview into a small box for the list card (PRD §3). This is data drawn from the
 * map JSON, not an uploaded picture.
 */
export function previewBox(
  preview: StudyMindMapPreview,
  width: number,
  height: number,
  padding = 12,
): PreviewBox {
  if (preview.nodes.length === 0) return { nodes: [], edges: [], width, height };
  const xs = preview.nodes.map((node) => node.x);
  const ys = preview.nodes.map((node) => node.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX || 1;
  const spanY = Math.max(...ys) - minY || 1;
  const scale = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY);
  const offsetX = (width - spanX * scale) / 2;
  const offsetY = (height - spanY * scale) / 2;
  const single = preview.nodes.length === 1;
  const point = (x: number, y: number) =>
    single
      ? { x: width / 2, y: height / 2 }
      : { x: offsetX + (x - minX) * scale, y: offsetY + (y - minY) * scale };
  const byId = new Map(preview.nodes.map((node) => [node.id, point(node.x, node.y)]));
  return {
    width,
    height,
    nodes: preview.nodes.map((node) => ({
      id: node.id,
      ...point(node.x, node.y),
      color: node.color,
      text: node.text,
    })),
    edges: preview.edges.flatMap((edge) => {
      const a = byId.get(edge.from);
      const b = byId.get(edge.to);
      return a && b
        ? [{ x1: a.x, y1: a.y, x2: b.x, y2: b.y, color: edge.color, line: edge.line }]
        : [];
    }),
  };
}

/** The preview of full content (draft preview and tests use the same drawing as the list). */
export function contentPreview(content: StudyMindMapContent): StudyMindMapPreview {
  return {
    nodes: content.nodes.map((node) => ({
      id: node.id,
      x: node.x,
      y: node.y,
      color: node.color,
      text: node.text.slice(0, 40),
    })),
    edges: content.edges.map((edge) => {
      const style = edgeStyleFields(edge);
      return {
        from: edge.from,
        to: edge.to,
        ...(style.color ? { color: style.color } : {}),
        ...(style.line ? { line: style.line } : {}),
      };
    }),
  };
}

export type TextSegment = { kind: 'text' | 'math'; value: string };

/** One maths part: `\(...\)` or `$...$` with no space just inside the dollars. */
const MATH_PATTERN = /\\\((.+?)\\\)|\$(?!\s)([^$\n]+?)(?<!\s)\$/g;

/**
 * Splits idea text into words and maths (PRD §4.3). Maths is `$...$` (typed by the learner) or
 * `\(...\)` (analysis text). A `$` followed by a space, such as "costs $5 and $6", stays text.
 */
export function mathSegments(text: string): TextSegment[] {
  const pattern = new RegExp(MATH_PATTERN);
  const segments: TextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ kind: 'text', value: text.slice(last, index) });
    segments.push({ kind: 'math', value: (match[1] ?? match[2] ?? '').trim() });
    last = index + match[0].length;
  }
  if (last < text.length) segments.push({ kind: 'text', value: text.slice(last) });
  return segments.filter((segment) => segment.value.length > 0);
}

/**
 * Shortens text with maths for a card preview (owner feedback 2026-10-09). The cut is made in the
 * source, before rendering, and never inside a `$...$` or `\(...\)` maths part: a maths part
 * that does not fit is left out whole. Plain words are cut at a space when one is near, and `…`
 * marks a cut.
 */
export function truncateMathText(text: string, max: number): string {
  if (text.length <= max) return text;
  let out = '';
  let last = 0;
  const parts: Array<{ math: boolean; value: string }> = [];
  for (const match of text.matchAll(new RegExp(MATH_PATTERN))) {
    const index = match.index ?? 0;
    if (index > last) parts.push({ math: false, value: text.slice(last, index) });
    parts.push({ math: true, value: match[0] });
    last = index + match[0].length;
  }
  if (last < text.length) parts.push({ math: false, value: text.slice(last) });
  for (const part of parts) {
    const room = max - out.length;
    if (part.value.length <= room) {
      out += part.value;
      continue;
    }
    if (!part.math) {
      const cut = part.value.slice(0, room);
      const space = cut.lastIndexOf(' ');
      out += space > room / 2 ? cut.slice(0, space) : cut;
    }
    break;
  }
  return `${out.trimEnd()}…`;
}
